import { bearing as turfBearing, point as turfPoint } from '@turf/turf';
import { ManeuverSign, signForAngle } from './maneuverSign';

/**
 * Turns that happen exactly where two planner segments meet.
 *
 * Every segment is routed between two waypoints, so GraphHopper opens each one with
 * "continue" — it has no idea the previous segment arrived from a different direction,
 * because it never saw it. A corner the planner put a marker on therefore arrives as sign 0
 * and is stripped as a seam artifact along with the genuine noise. The turn is invisible in
 * the instructions but plainly there in the geometry, which is what this reads.
 *
 * The extreme case is an out-and-back turnaround: the halfway point is a 180° reversal that
 * nothing announces at all.
 *
 * Deliberately additive — a seam is only reported when nothing already covers it, so a route
 * whose instructions are complete comes out unchanged.
 */

export interface PointWithDistance {
  /** [lng, lat] */
  coordinates: number[];
  distanceMeters: number;
}

export interface Seam {
  distanceFromStartMeters: number;
  sign: number;
}

/**
 * How far either side of the seam to sample the heading. A single vertex pair can be a metre
 * long and its bearing is mostly noise; a stretch either side is the direction a walker
 * actually experiences.
 */
const WINDOW_METERS = 20;

/** A seam this close to a real instruction is that instruction, seen from the geometry. */
const MIN_SEPARATION_METERS = 25;

export function detectSeams(
  points: PointWithDistance[],
  seamDistances: number[],
  existingManeuverDistances: number[]
): Seam[] {
  if (points.length < 2 || seamDistances.length === 0) return [];

  const seams: Seam[] = [];
  seamDistances.forEach((seam) => {
    const covered = existingManeuverDistances.some(
      (d) => Math.abs(d - seam) < MIN_SEPARATION_METERS
    );
    if (covered) return;

    const headings = headingsAt(points, seam);
    if (!headings) return;

    const sign = signForAngle(signedTurn(headings[0], headings[1]));
    if (sign === ManeuverSign.CONTINUE) return;
    seams.push({ distanceFromStartMeters: seam, sign });
  });
  return seams;
}

/**
 * Heading into and out of the seam.
 *
 * Anchored on the vertex at the join, then walking outwards for something to measure against.
 * The window is a preference, not a requirement: trail geometry is sparse where it is
 * straight, and a route with 45 m between vertices at its turnaround would otherwise pick the
 * anchor itself, leaving no angle to compute and the turn unreported. A vertex further out
 * than asked for still gives the right heading; the same vertex gives nothing.
 */
function headingsAt(
  points: PointWithDistance[],
  seam: number
): [number, number] | undefined {
  let anchorIndex = 0;
  let best = Infinity;
  points.forEach((p, i) => {
    const gap = Math.abs(p.distanceMeters - seam);
    if (gap < best) {
      best = gap;
      anchorIndex = i;
    }
  });
  const anchor = points[anchorIndex].coordinates;

  const before =
    findIndex(points, anchorIndex - 1, -1, (p) => p.distanceMeters <= seam - WINDOW_METERS && !sameLocation(p.coordinates, anchor)) ??
    findIndex(points, anchorIndex - 1, -1, (p) => !sameLocation(p.coordinates, anchor));

  const after =
    findIndex(points, anchorIndex + 1, 1, (p) => p.distanceMeters >= seam + WINDOW_METERS && !sameLocation(p.coordinates, anchor)) ??
    findIndex(points, anchorIndex + 1, 1, (p) => !sameLocation(p.coordinates, anchor));

  if (before === undefined || after === undefined) return undefined;

  return [
    turfBearing(turfPoint(points[before].coordinates), turfPoint(anchor)),
    turfBearing(turfPoint(anchor), turfPoint(points[after].coordinates))
  ];
}

function findIndex(
  points: PointWithDistance[],
  from: number,
  step: number,
  match: (p: PointWithDistance) => boolean
): number | undefined {
  for (let i = from; i >= 0 && i < points.length; i += step) {
    if (match(points[i])) return i;
  }
  return undefined;
}

/** A segment join repeats its coordinate, once as an end and once as the next start. */
function sameLocation(a: number[], b: number[]): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/** Turn angle in (-180, 180]; positive is a right turn, matching `signForAngle`. */
function signedTurn(incomingBearing: number, outgoingBearing: number): number {
  let delta = outgoingBearing - incomingBearing;
  while (delta <= -180) delta += 360;
  while (delta > 180) delta -= 360;
  return delta;
}
